import { useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode, type Ref } from 'react';
import { Check, ChevronDown, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { AgentChatSummary, AgentMessage, FiledDocument, Agent, SuggestedQuestion } from '../lib/types';
import { whatItDoes } from '../lib/agents';
import AgentChat, { type AgentChatHandle } from './AgentChat';
import { AttachButton, AttachmentChips, useChatAttachments } from './ChatAttachments';

// The conversation with the agent, available on every page. One
// conversation per person, resumed where they left off. The agent uses at most
// one skill at a time: the person picks it here or with a message opening
// `/<skill>`, and the agent can take one up itself. On a business area's page
// the agent works from that area, and the area's suggested questions start a
// conversation with the skill each names. The `agents` list is the agent (its
// `default` entry) followed by its skills.

// The skills, under the one in use or "Skills" when there is none. Choosing a skill
// takes it up in place of any other; choosing the one in use stops using it.
function SkillPicker({
  skills, current, onChoose,
}: {
  skills: Agent[];
  current: Agent | undefined;
  onChoose: (key: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (skills.length === 0) return null;
  const name = current?.name ?? 'Skills';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={current ? `Skill in use: ${name}. Choose another or stop using it` : 'Choose a skill'}
        title={current ? whatItDoes(current.description) : undefined}
        className="flex items-center gap-1.5 -ml-1.5 px-1.5 py-1 rounded-md text-xs font-medium text-graphite hover:bg-offwhite transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5 shrink-0 text-brand-dark" />
        <span className="whitespace-nowrap">{name}</span>
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-coolslate transition-transform ${open ? '' : 'rotate-180'}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Skills"
          className="absolute bottom-full left-0 mb-1 w-72 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto navaris-dropdown z-30"
        >
          {skills.map(s => {
            const selected = s.key === current?.key;
            return (
              <li key={s.key} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => { onChoose(selected ? null : s.key); setOpen(false); }}
                  title={selected ? 'Stop using this skill' : undefined}
                  className={`w-full text-left flex items-start gap-2 px-3 py-2 transition-colors ${
                    selected ? 'bg-brand/10' : 'hover:bg-offwhite'
                  }`}
                >
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 text-brand-dark ${selected ? '' : 'invisible'}`} />
                  <span className="min-w-0">
                    <span className={`block text-sm ${selected ? 'font-semibold text-brand-dark' : 'text-graphite'}`}>{s.name}</span>
                    {s.description && (
                      <span className="block mt-0.5 text-xs text-coolslate leading-snug">{whatItDoes(s.description)}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export interface AgentCardHandle {
  startChat: (agent?: string, ask?: string) => Promise<void>;
  openChat: (id: string) => Promise<void>;
}

export default function AgentCard({
  ref, sectionId, onOpenDocument, openDocumentId, actions, onNavigate,
}: {
  ref?: Ref<AgentCardHandle>;
  onNavigate?: () => void;
  sectionId: string | null;
  actions?: ReactNode;
  onOpenDocument?: (doc: AgentMessage) => void;
  openDocumentId?: string | null;
}) {
  const { authFetch } = useAuth();
  const { agents, chats, refresh } = useWorkspace();
  const [chatId, setChatId] = useState<string | null>(null);
  // null = no skill.
  const [skill, setSkill] = useState<string | null>(null);
  const [loadedTitle, setLoadedTitle] = useState<string | null>(null);
  const [questions, setQuestions] = useState<SuggestedQuestion[]>([]);
  const [filed, setFiled] = useState<FiledDocument[]>([]);
  const chatRef = useRef<AgentChatHandle>(null);
  // Set once the person picks or starts a conversation, so the resume below leaves it alone.
  const chosen = useRef(false);

  useEffect(() => {
    let stale = false;
    authFetch('/agents/chats')
      .then(r => r.ok ? r.json() : [])
      .then((chats: AgentChatSummary[]) => {
        if (stale || chosen.current || chats.length === 0) return;
        setChatId(chats[0].id);
        setSkill(chats[0].skill);
        setLoadedTitle(chats[0].title ?? null);
      })
      .catch(() => {});
    return () => { stale = true; };
  }, [authFetch]);

  useEffect(() => {
    if (!sectionId) { setQuestions([]); setFiled([]); return; }
    let stale = false;
    authFetch(`/agents/context?section_id=${encodeURIComponent(sectionId)}`)
      .then(r => r.ok ? r.json() : { questions: [], filed: [] })
      .then(data => {
        if (stale) return;
        setQuestions(data.questions ?? []);
        setFiled(data.filed ?? []);
      })
      .catch(() => {});
    return () => { stale = true; };
  }, [sectionId, authFetch]);

  // The card owns the chat id so attaching a document can create the conversation before the
  // first message; a message and an attachment arriving together share one creation.
  const createChat = useCallback(async (key: string | null): Promise<string | null> => {
    const res = await authFetch('/agents/chats', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skill: key }),
    });
    return res.ok ? (await res.json()).id as string : null;
  }, [authFetch]);

  const creating = useRef<Promise<string | null> | null>(null);
  const ensureChat = useCallback(async (): Promise<string | null> => {
    if (chatId) return chatId;
    if (!creating.current) {
      creating.current = createChat(skill)
        .then(created => { if (created) setChatId(created); return created; })
        .finally(() => { creating.current = null; });
    }
    return creating.current;
  }, [skill, chatId, createChat]);

  // The agent's own entry means no skill.
  const skillOf = useCallback((key: string | null | undefined) =>
    key && agents.some(s => s.key === key && !s.default) ? key : null, [agents]);

  const switchSkill = useCallback(async (key: string | null) => {
    const next = skillOf(key);
    setSkill(next);
    if (!chatId) return;
    const res = await authFetch(`/agents/chats/${chatId}/skill`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skill: next }),
    }).catch(() => null);
    // The switch is noted in the conversation.
    if (res?.ok) chatRef.current?.reload();
  }, [authFetch, chatId, skillOf]);

  useImperativeHandle(ref, () => ({
    startChat: async (agent, ask) => {
      chosen.current = true;
      const key = skillOf(agent);
      chatRef.current?.reset();
      setChatId(null);
      setSkill(key);
      setLoadedTitle(null);
      if (!ask) return;
      // Created here because the chat's own view of its conversation only updates on the next render.
      const id = await createChat(key);
      if (!id) return;
      chatRef.current?.send(ask, id);
      setChatId(id);
    },
    openChat: async (id) => {
      chosen.current = true;
      const res = await authFetch(`/agents/chats/${id}`).catch(() => null);
      if (!res?.ok) return;
      const chat = await res.json();
      if (chat.id !== chatId) chatRef.current?.reset();
      setChatId(chat.id);
      setSkill(chat.skill ?? null);
      setLoadedTitle(chat.title ?? null);
    },
  }));

  // A turn may have titled or reordered this person's chats.
  const turnDone = useCallback((key: string | null) => {
    setSkill(key);
    refresh();
  }, [refresh]);

  const attachments = useChatAttachments(chatId, ensureChat);
  const current = skill ? agents.find(s => s.key === skill && !s.default) : undefined;
  const chatTitle = (chatId && chats.find(c => c.id === chatId)?.title) || loadedTitle;
  // A question naming no skill is for the agent itself.
  const navaris = agents.find(s => s.default);
  const nameOf = (key: string) => agents.find(s => s.key === key)?.name ?? `/${key}`;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 shrink-0 border-b border-softgrey">
        <div className="flex items-center justify-between gap-2">
          <h4 className="min-w-0 truncate text-sm font-semibold text-graphite" title={chatTitle ?? undefined}>
            {chatTitle || 'New agent'}
          </h4>
          <div className="flex items-center gap-0.5 -my-1 -mr-1 shrink-0">{actions}</div>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <AgentChat
          ref={chatRef}
          chatId={chatId}
          ensureChat={ensureChat}
          agents={agents}
          onSwitch={switchSkill}
          onTurnDone={turnDone}
          onNavigate={onNavigate}
          onOpenDocument={onOpenDocument}
          openDocumentId={openDocumentId}
          emptyState={
            <div className="py-6 px-2 space-y-3">
              <p className="text-sm text-coolslate text-center">
                Ask about the company, attach a document to talk about, or type / to use a skill.
              </p>
              {questions.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {questions.map(q => (
                    <button
                      key={`${q.skill}:${q.text}`}
                      onClick={() => chatRef.current?.send(`/${q.skill ?? navaris?.key} ${q.text}`)}
                      className="text-left bg-white border border-softgrey rounded-lg px-3 py-2 hover:border-brand/40"
                    >
                      <span className="block text-xs text-brand-dark">{q.text}</span>
                      <span className="block mt-0.5 text-[11px] text-coolslate">{q.skill ? nameOf(q.skill) : navaris?.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          }
          composerHeader={<SkillPicker skills={agents.filter(s => !s.default)} current={current} onChoose={switchSkill} />}
          composerFooter={<AttachmentChips state={attachments} />}
          inputAdornment={<AttachButton state={attachments} filed={filed} />}
        />
      </div>
    </div>
  );
}
